<template>
  <div>
    <WdsRadioGroup
      v-model:checked="allowSoletraderEngagement"
      label="Will your company allow direct engagement with Sole Traders?"
      name="allow_soletrader_engagement"
    >
      <WdsRadio value="unknown">Not sure</WdsRadio>
      <WdsRadio :value="0">
        Deny direct engagement with Sole Traders
        <br />
        <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
        <WdsText class="tw-mt-2" secondary>
          Do not allow direct payments to Sole Traders, but instead force them on e.g. PAYE
          contracts.
        </WdsText>
      </WdsRadio>
      <WdsRadio :value="1">Allow direct engagement with Sole Traders</WdsRadio>
    </WdsRadioGroup>

    <WdsAlert v-if="totalSoleTraderHires > 0" class="tw-mt-4" icon info>
      Your company has active Sole Trader contracts
      <template #description>
        You have {{ totalSoleTraderHires }} active contracts with Sole Traders.
        <p class="tw-mt-2 tw-mb-0">
          Changing whether you allow direct engagement with Sole Traders may impact these contracts.
        </p>
      </template>
    </WdsAlert>

    <WdsAlert
      v-if="errorMessage && showError"
      class="tw-mt-2"
      closable
      error
      @close="showError = false"
    >
      {{ $t('An error occurred') }}
      <template #description>
        {{ errorMessage }}
      </template>
    </WdsAlert>
    <WdsAlert v-else-if="showSuccess" class="tw-mt-2" closable success @close="showSuccess = false">
      {{ $t('Thanks, your settings has been updated') }}
    </WdsAlert>

    <div class="tw-mt-4">
      <WdsButton :disabled="loading" primary @click="onSave">
        <div class="tw-flex tw-items-center">
          <span>Save</span>
          <WdsSpinner v-show="loading" class="tw-ml-2" indeterminate>
            <WdsIcon name="bam" :size="16" />
          </WdsSpinner>
        </div>
      </WdsButton>
    </div>
  </div>
</template>

<script>
import { reactive, ref } from 'vue'
import {
  WdsAlert,
  WdsButton,
  WdsIcon,
  WdsRadio,
  WdsRadioGroup,
  WdsSpinner,
} from '@worksome/wds-components'
import { UpdateComplianceMutation } from '@/components/compliances/mutations/UpdateComplianceMutation'
import { FormValidation } from '@/utils/formValidation'
import { booleanToIntNullable, intToBooleanNullable } from '@/utils/forms/ValueNormalisers'

export default {
  components: {
    WdsAlert,
    WdsButton,
    WdsIcon,
    WdsRadio,
    WdsRadioGroup,
    WdsSpinner,
  },
  props: {
    company: {
      type: Object,
      required: true,
    },
    complianceData: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const errors = ref([])
    const internalComplianceData = reactive(props.complianceData)
    const loading = ref(false)
    const showError = ref(false)
    const showSuccess = ref(false)

    return {
      errors,
      internalComplianceData,
      loading,
      showError,
      showSuccess,
    }
  },
  computed: {
    allowSoletraderEngagement: {
      get() {
        const { allowSoletraderEngagement } = this.internalComplianceData
        if (allowSoletraderEngagement === null) {
          return 'unknown'
        }
        return booleanToIntNullable(allowSoletraderEngagement)
      },
      set(allowSoletraderEngagement) {
        let value = null
        if (allowSoletraderEngagement !== 'unknown') {
          value = intToBooleanNullable(allowSoletraderEngagement)
        }
        this.internalComplianceData.allowSoletraderEngagement = value
        this.$emit('update:complianceData', this.internalComplianceData)
      },
    },
    errorMessage() {
      return FormValidation.containsErrorMessage(this.errors)
    },
    totalSoleTraderHires() {
      return this.internalComplianceData.totalSoleTraderHires ?? 0
    },
  },
  methods: {
    async onSave() {
      this.loading = true

      const data = JSON.stringify({
        allow_soletrader_engagement: this.internalComplianceData.allowSoletraderEngagement,
      })

      try {
        await this.$apollo.mutate({
          mutation: UpdateComplianceMutation,
          variables: {
            input: {
              name: 'UK_SOLE_TRADER',
              data,
            },
          },
        })

        this.showSuccess = true
      } catch ({ graphQLErrors }) {
        this.errors = graphQLErrors
        this.showError = true
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
